import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDataProvider, Loading, Error, Title, useNotify } from "react-admin";
import { Button, IconButton, ButtonGroup, Box } from "@material-ui/core";
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  FilterList as FilterListIcon,
  Adjust as AdjustIcon,
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ListAlt as ListAltIcon,
  SentimentVeryDissatisfied as DontHaveIcon,
} from "@material-ui/icons";
import { cloneDeep } from "lodash-es";
import { getPrevMonthDay, getNextMonthDay, getYearMonthKor } from "../calendar";

import {
  reqSchedules,
  reqSuggestion,
  reqSaveSchedules,
  reqCustomer,
  reqPbMenus,
  reqCustomerFCS,
} from "./api";
import { handleScheduleResult, handleSuggestionResult } from "./functions";
import {
  SET_CUSTOMER,
  SET_FIRSTDATE,
  SET_MEALS,
  SET_SUGGESTION,
  OPEN_DLG_CUSTOMER_LIST,
  selectMeals,
  selectCustomer,
  selectFirstDate,
  ADD_DONTHAVE,
  REMOVE_DONTHAVE,
  selectCurationGroupId,
  SET_CURATION_GROUP_ID,
} from "./reducer";
import { selectCustomerLockedMeals } from "../order-schedule";
import CustomerOrderCalendar from "./CustomerOrderCalendar";
import CuratedMenuList from "./CuratedMenuList";
import AllMenuList from "./AllMenuList";
import FilterMenuDialog from "./FilterMenuDialog";
import MenuCategoryRatio from "./MenuCategoryRatio";

import styles from "./CustomerOrderList.module.css";
import { ServiceTimes } from "./ServiceTimes";

const defaultState = {
  hasDontHave: true,
  lockCuration: true,
  openFilterMenuDlg: false,
};

const CustomerOrderList = ({}) => {
  const dispatch = useDispatch();

  const dataProvider = useDataProvider();
  const notify = useNotify();

  const dataRef = useRef({
    // schedules: [],
    // suggestions: [],
    customer: null,
  });

  // 선택된 고객
  const customer = useSelector(selectCustomer);

  // 선택된 month의 first date
  const firstDate = useSelector(selectFirstDate);

  // lock 상태 orders 정보
  const lockedMeals = useSelector((state) =>
    selectCustomerLockedMeals(state, customer.id)
  );

  // meals data
  // LATER: selector 이외 방식 알아보기
  const meals = useSelector(selectMeals);

  const curationGroupId = useSelector(selectCurationGroupId);

  const [menuRatio, setMenuRatio] = useState([]);
  const [state, setState] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [error, setError] = useState();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // load order schedules
        const { data } = await reqSchedules(
          dataProvider,
          customer.id,
          firstDate
        );

        // load service time info
        const { data: customerData } = await reqCustomer(customer.id);

        // get curationGroupId
        const { data: customerFCS } = await reqCustomerFCS(customer.id);
        const curationGroupId = customerFCS.curationGroupId;
        dispatch({
          type: SET_CURATION_GROUP_ID,
          payload: {
            curationGroupId,
          },
        });

        // load pb menus
        const { data: pbMenus } = await reqPbMenus(customer.id);

        // update menu ratio if customer changed
        if (dataRef.current.customer?.id !== customer.id) {
          setMenuRatio(cloneDeep(customerData.menuRatio));
        }

        // set order schedules
        dispatch({
          type: SET_MEALS,
          payload: {
            meals: handleScheduleResult(data),
            lockedMeals,
            customer: customerData,
            pbMenus,
          },
        });

        // keep some data for later ...
        dataRef.current.customer = customerData;
      } catch (err) {
        setError(err);
        // LATER server logging
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customer, firstDate]);
  // }, [ customer.id, firstDate ]);

  const handleClickPrev = () => {
    dispatch({
      type: SET_FIRSTDATE,
      payload: {
        firstDate: getPrevMonthDay(firstDate),
      },
    });
  };

  const handleClickNext = () => {
    dispatch({
      type: SET_FIRSTDATE,
      payload: {
        firstDate: getNextMonthDay(firstDate),
      },
    });
  };

  const handleClickChangeCustomer = () => {
    dispatch({
      type: OPEN_DLG_CUSTOMER_LIST,
    });
  };

  const handleClickLockCuration = () => {
    setState(Object.assign({}, state, { lockCuration: !state.lockCuration }));
  };

  const handleClickCuration = async () => {
    setLoading(true);
    try {
      // request curation
      const { data } = await reqSuggestion(
        customer.id,
        firstDate,
        meals,
        menuRatio
      );
      dataRef.current.sugestions = data;

      // set suggestions
      dispatch({
        type: SET_SUGGESTION,
        payload: {
          suggestions: handleSuggestionResult(data),
        },
      });
    } catch (err) {
      setError(err);
      // LATER server logging
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClickSave = async () => {
    setLoadingSave(true);
    try {
      // 저장 요청
      const { data } = await reqSaveSchedules(meals);
      // refresh
      reqRefresh();
    } catch (err) {
      notify(
        `저장에 실패했습니다. 관리자에게 문의해주세요. \n오류: ${err}`,
        "error"
      );
      console.error(err);
    } finally {
      setLoadingSave(false);
    }
  };

  const handleChangeMenuRatio = (newMenuRatio) => {
    setMenuRatio(newMenuRatio);
  };

  const handleClickDontHave = () => {
    dispatch({
      type: state.hasDontHave ? REMOVE_DONTHAVE : ADD_DONTHAVE,
    });

    // toggle hasDontHave value
    setState(Object.assign({}, state, { hasDontHave: !state.hasDontHave }));
  };

  const reqRefresh = () => {
    // 페이지 다시 로딩 유도
    dispatch({
      type: SET_CUSTOMER,
      payload: {
        customer: cloneDeep(customer),
      },
    });
  };

  const handleClickFilterMenu = () => {
    setState(Object.assign({}, state, { openFilterMenuDlg: true }));
  };

  const handleFilterMenuDlgClose = () => {
    setState(Object.assign({}, state, { openFilterMenuDlg: false }));
  };

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  return (
    <div>
      <h1>기업 큐레이션</h1>
      <div className={styles.content}>
        {/* summary */}
        <div className={styles.summary}>
          <div className={styles.customer}>
            <span>
              {customer.name}
              <small>{`(ID:${customer.id})`}</small>
            </span>
            <Box sx={{ bgcolor: "beige", marginTop: 1 }}>
              <small>큐레이션 그룹 ID: {curationGroupId ?? "없음"}</small>
            </Box>
            <IconButton
              aria-label="change customer"
              onClick={handleClickChangeCustomer}
            >
              <ListAltIcon />
            </IconButton>
          </div>
          <div>
            <MenuCategoryRatio
              menuRatio={menuRatio}
              onRatioChange={handleChangeMenuRatio}
            />
          </div>
          <div>
            <ServiceTimes />
          </div>
          {/* <div>요약-금액</div>
          <div>요약-평점</div> */}
        </div>
        {/* schedules */}
        {/* calendar actions */}
        <div className={styles.calendarToolbar}>
          <div>
            <span>{getYearMonthKor(firstDate)}</span>
            <IconButton aria-label="go to prev" onClick={handleClickPrev}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton aria-label="go to next" onClick={handleClickNext}>
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className={styles.calendarActions}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleClickFilterMenu}
              style={{ marginRight: "5px" }}
            >
              필터
            </Button>
            <Button
              variant="outlined"
              startIcon={<DontHaveIcon />}
              onClick={handleClickDontHave}
              style={{ marginRight: "5px" }}
            >
              {state.hasDontHave ? "안먹어요 삭제" : "안먹어요 추가"}
            </Button>
            <ButtonGroup style={{ marginRight: "5px" }}>
              <Button
                variant="outlined"
                startIcon={state.lockCuration ? <LockIcon /> : <LockOpenIcon />}
                onClick={handleClickLockCuration}
              />
              <Button
                variant="outlined"
                startIcon={<AdjustIcon />}
                onClick={handleClickCuration}
                disabled={state.lockCuration}
              >
                큐레이션
              </Button>
            </ButtonGroup>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleClickSave}
              disabled={loadingSave}
            >
              저장하기
            </Button>
          </div>
        </div>
        {/* calendar */}
        <CustomerOrderCalendar date={firstDate} />
        {/* menu list column */}
        <div className={styles.menus}>
          {/* <div>
            <CuratedMenuList
              customerId={customer.id}
            />
          </div> */}
          <div>
            <AllMenuList />
          </div>
        </div>
      </div>
      <FilterMenuDialog
        customerId={customer?.id}
        open={state.openFilterMenuDlg}
        onClose={handleFilterMenuDlgClose}
      />
    </div>
  );
};

export default CustomerOrderList;
